import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner
} from 'react-bootstrap';
import {
  FaCamera,
  FaDownload,
  FaEye,
  FaIdCard,
  FaLink,
  FaPrint,
  FaRedo,
  FaSave,
  FaSearch,
  FaTrash,
  FaUserCheck
} from 'react-icons/fa';
import EmployeeBadgeCard from '../components/EmployeeBadgeCard';
import { AuthContext } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import employeeBadgeService from '../services/employeeBadgeService';
import { prepareBadgePhoto } from '../utils/badgePhoto';
import { formatNumber } from '../utils/format';
import { normalizeRole } from '../utils/rbac';
import '../styles/employee-badges.css';

const EMPTY_PROFILE_FORM = {
  job_title: '',
  department_phone: ''
};

const STATUS_FILTERS = [
  { value: 'all', label: 'الكل' },
  { value: 'active', label: 'فعالة' },
  { value: 'disabled', label: 'معطلة' },
  { value: 'none', label: 'بدون بطاقة' }
];

const getUserBadgeState = (user) => {
  if (!user?.has_badge) return 'none';
  return user.badge_status || 'none';
};

const EmployeeBadges = () => {
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const { user } = useContext(AuthContext);
  const { addNotification } = useNotification();
  const currentRole = normalizeRole(user?.role);
  const canManageBadges = currentRole === 'admin' || currentRole === 'supervisor';

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionKey, setActionKey] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [profileForm, setProfileForm] = useState(EMPTY_PROFILE_FORM);
  const [inlineMessage, setInlineMessage] = useState(null);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [pendingLogo, setPendingLogo] = useState(null);

  const revokePreviewUrl = useCallback((previewUrl) => {
    if (previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  }, []);

  const updateUsersFromDetail = useCallback((nextDetail) => {
    if (!nextDetail?.user?.id) return;

    setUsers((previousUsers) => previousUsers.map((item) => (
      Number(item.id) === Number(nextDetail.user.id)
        ? {
            ...item,
            full_name: nextDetail.user.full_name,
            email: nextDetail.user.email,
            phone: nextDetail.user.phone,
            role: nextDetail.user.role,
            role_label_ar: nextDetail.user.role_label_ar,
            job_title: nextDetail.user.job_title,
            staff_code: nextDetail.user.staff_code,
            department_number: nextDetail.user.department_number,
            department_phone: nextDetail.user.department_phone,
            is_active: nextDetail.user.is_active,
            profile_image_url: nextDetail.user.profile_image_url,
            has_badge: nextDetail.badge.exists,
            badge_serial: nextDetail.badge.badge_serial,
            badge_status: nextDetail.badge.badge_status,
            badge_status_label_ar: nextDetail.badge.badge_status_label_ar,
            qr_enabled: nextDetail.badge.qr_enabled,
            card_generated_at: nextDetail.badge.card_generated_at,
            qr_last_generated_at: nextDetail.badge.qr_last_generated_at
          }
        : item
    )));
  }, []);

  const applyDetail = useCallback((nextDetail) => {
    setDetail(nextDetail);
    setSelectedUserId(nextDetail?.user?.id || null);
    setProfileForm({
      job_title: nextDetail?.user?.job_title || '',
      department_phone: nextDetail?.user?.department_phone || ''
    });
    updateUsersFromDetail(nextDetail);
  }, [updateUsersFromDetail]);

  useEffect(() => () => {
    revokePreviewUrl(pendingPhoto?.previewUrl);
    revokePreviewUrl(pendingLogo?.previewUrl);
  }, [pendingLogo, pendingPhoto, revokePreviewUrl]);

  useEffect(() => {
    if (!canManageBadges) return undefined;

    let isMounted = true;

    const loadDashboard = async () => {
      try {
        setLoading(true);
        const response = await employeeBadgeService.getDashboard();
        if (!isMounted) return;
        setUsers(response.users || []);
        if (response.selected) {
          applyDetail(response.selected);
        } else {
          setDetail(null);
        }
      } catch (error) {
        const message = error.response?.data?.error || 'تعذر تحميل شاشة بطاقات التعريف';
        if (isMounted) {
          setInlineMessage({ type: 'danger', text: message });
        }
        addNotification(message, 'error', 'تعذر التحميل');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, [addNotification, applyDetail, canManageBadges]);

  const filteredUsers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return users.filter((item) => {
      const matchesSearch = !normalizedSearch || [
        item.full_name,
        item.email,
        item.phone,
        item.staff_code,
        item.job_title,
        item.department_number,
        item.department_phone
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch));

      const badgeState = getUserBadgeState(item);
      const matchesStatus = statusFilter === 'all' || badgeState === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [searchTerm, statusFilter, users]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((item) => item.badge_status === 'active').length,
    disabled: users.filter((item) => item.badge_status === 'disabled').length,
    none: users.filter((item) => !item.has_badge).length
  }), [users]);

  const previewDetail = useMemo(() => {
    if (!detail) return null;
    const nextUser = pendingPhoto?.previewUrl
      ? {
          ...detail.user,
          profile_image_url: pendingPhoto.previewUrl
        }
      : detail.user;
    const nextCompanyIdentity = pendingLogo?.previewUrl
      ? {
          ...detail.company_identity,
          logo_data_uri: pendingLogo.previewUrl,
          has_custom_logo: true
        }
      : detail.company_identity;

    if (nextUser === detail.user && nextCompanyIdentity === detail.company_identity) {
      return detail;
    }

    return {
      ...detail,
      user: nextUser,
      company_identity: nextCompanyIdentity
    };
  }, [detail, pendingLogo, pendingPhoto]);

  const applyCompanyIdentity = useCallback((nextCompanyIdentity) => {
    if (!nextCompanyIdentity) return;

    setDetail((previousDetail) => {
      if (!previousDetail) return previousDetail;

      return {
        ...previousDetail,
        company_identity: nextCompanyIdentity,
        display: {
          ...(previousDetail.display || {}),
          company_address_line:
            nextCompanyIdentity.company_address_line ||
            previousDetail.display?.company_address_line ||
            ''
        }
      };
    });
  }, []);

  const handleSelectUser = async (userId) => {
    if (!userId || Number(userId) === Number(selectedUserId)) return;

    try {
      setDetailLoading(true);
      revokePreviewUrl(pendingPhoto?.previewUrl);
      revokePreviewUrl(pendingLogo?.previewUrl);
      setPendingPhoto(null);
      setPendingLogo(null);
      const response = await employeeBadgeService.getByUser(userId);
      applyDetail(response);
      setInlineMessage(null);
    } catch (error) {
      const message = error.response?.data?.error || 'تعذر تحميل بطاقة الموظف';
      setInlineMessage({ type: 'danger', text: message });
      addNotification(message, 'error', 'تعذر التحميل');
    } finally {
      setDetailLoading(false);
    }
  };

  const runAction = async (key, task, successTitle) => {
    try {
      setActionKey(key);
      const response = await task();
      if (response?.detail) {
        applyDetail(response.detail);
      }
      if (response?.message) {
        setInlineMessage({ type: 'success', text: response.message });
        addNotification(response.message, 'success', successTitle);
      }
    } catch (error) {
      const message = error.response?.data?.error || 'حدث خطأ غير متوقع';
      setInlineMessage({ type: 'danger', text: message });
      addNotification(message, 'error', 'تعذر التنفيذ');
    } finally {
      setActionKey('');
    }
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    if (!detail?.user?.id) return;

    await runAction(
      'save-profile',
      () => employeeBadgeService.saveProfile(detail.user.id, profileForm),
      'تم التحديث'
    );
  };

  const handleCreateBadge = async () => {
    if (!detail?.user?.id) return;

    await runAction(
      'create-badge',
      () => employeeBadgeService.createBadge(detail.user.id),
      'تم إنشاء البطاقة'
    );
  };

  const handleRegenerateQr = async () => {
    if (!detail?.user?.id) return;

    await runAction(
      'regenerate-qr',
      () => employeeBadgeService.regenerateQr(detail.user.id),
      'تم تحديث QR'
    );
  };

  const handleDisableBadge = async () => {
    if (!detail?.user?.id) return;

    await runAction(
      'disable-badge',
      () => employeeBadgeService.disableBadge(detail.user.id),
      'تم تعطيل البطاقة'
    );
  };

  const handlePhotoPick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const prepared = await prepareBadgePhoto(file);
      revokePreviewUrl(pendingPhoto?.previewUrl);
      setPendingPhoto(prepared);
      setInlineMessage({
        type: 'info',
        text: 'تم تجهيز الصورة ومعاينتها. اضغط "رفع الصورة" لحفظها على النظام.'
      });
    } catch (error) {
      const message = error.message || 'تعذر تجهيز الصورة';
      setInlineMessage({ type: 'danger', text: message });
      addNotification(message, 'warning', 'تحقق من الصورة');
    } finally {
      event.target.value = '';
    }
  };

  const handleUploadPhoto = async () => {
    if (!detail?.user?.id || !pendingPhoto?.file) return;

    await runAction(
      'upload-photo',
      async () => {
        const response = await employeeBadgeService.uploadPhoto(detail.user.id, pendingPhoto.file);
        revokePreviewUrl(pendingPhoto.previewUrl);
        setPendingPhoto(null);
        return response;
      },
      'تم رفع الصورة'
    );
  };

  const handleDeletePhoto = async () => {
    if (!detail?.user?.id) return;

    if (pendingPhoto?.previewUrl?.startsWith('blob:')) {
      revokePreviewUrl(pendingPhoto.previewUrl);
      setPendingPhoto(null);
      setInlineMessage({ type: 'info', text: 'تمت إزالة المعاينة المحلية للصورة' });
      return;
    }

    await runAction(
      'delete-photo',
      () => employeeBadgeService.deletePhoto(detail.user.id),
      'تم حذف الصورة'
    );
  };

  const handleLogoPick = () => {
    logoInputRef.current?.click();
  };

  const handleLogoSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!['jpg', 'jpeg', 'png'].includes(extension || '')) {
      const message = 'الصيغ المسموحة للشعار هي JPG وJPEG وPNG فقط.';
      setInlineMessage({ type: 'danger', text: message });
      addNotification(message, 'warning', 'تحقق من الشعار');
      event.target.value = '';
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      const message = 'حجم الشعار يجب ألا يتجاوز 3 ميجابايت.';
      setInlineMessage({ type: 'danger', text: message });
      addNotification(message, 'warning', 'تحقق من الشعار');
      event.target.value = '';
      return;
    }

    revokePreviewUrl(pendingLogo?.previewUrl);
    setPendingLogo({
      file,
      previewUrl: URL.createObjectURL(file)
    });
    setInlineMessage({
      type: 'info',
      text: 'تمت معاينة شعار الشركة. اضغط "رفع الشعار" لاعتماده على جميع البطاقات.'
    });
    event.target.value = '';
  };

  const handleUploadLogo = async () => {
    if (!pendingLogo?.file) return;

    try {
      setActionKey('upload-logo');
      const response = await employeeBadgeService.uploadCompanyLogo(pendingLogo.file);
      revokePreviewUrl(pendingLogo.previewUrl);
      setPendingLogo(null);
      applyCompanyIdentity(response.company_identity);
      if (response?.message) {
        setInlineMessage({ type: 'success', text: response.message });
        addNotification(response.message, 'success', 'تم تحديث الشعار');
      }
    } catch (error) {
      const message = error.response?.data?.error || 'تعذر رفع شعار الشركة';
      setInlineMessage({ type: 'danger', text: message });
      addNotification(message, 'error', 'تعذر التنفيذ');
    } finally {
      setActionKey('');
    }
  };

  const handleDeleteLogo = async () => {
    if (pendingLogo?.previewUrl?.startsWith('blob:')) {
      revokePreviewUrl(pendingLogo.previewUrl);
      setPendingLogo(null);
      setInlineMessage({ type: 'info', text: 'تمت إزالة المعاينة المحلية للشعار' });
      return;
    }

    if (!detail?.company_identity?.has_custom_logo) return;

    try {
      setActionKey('delete-logo');
      const response = await employeeBadgeService.deleteCompanyLogo();
      applyCompanyIdentity(response.company_identity);
      if (response?.message) {
        setInlineMessage({ type: 'success', text: response.message });
        addNotification(response.message, 'success', 'تم حذف الشعار');
      }
    } catch (error) {
      const message = error.response?.data?.error || 'تعذر حذف شعار الشركة';
      setInlineMessage({ type: 'danger', text: message });
      addNotification(message, 'error', 'تعذر التنفيذ');
    } finally {
      setActionKey('');
    }
  };

  const handleDownloadPdf = async () => {
    if (!detail?.user?.id || !detail?.badge?.exists) return;

    try {
      setActionKey('download-pdf');
      await employeeBadgeService.downloadPdf(detail.user.id);
      addNotification('تم بدء تنزيل ملف البطاقة بصيغة PDF', 'success', 'تنزيل البطاقة');
    } catch (error) {
      const message = error.response?.data?.error || 'تعذر تنزيل البطاقة';
      setInlineMessage({ type: 'danger', text: message });
      addNotification(message, 'error', 'تعذر التنزيل');
    } finally {
      setActionKey('');
    }
  };

  const handlePrint = () => {
    if (!detail?.badge?.print_route) return;
    window.open(detail.badge.print_route, '_blank', 'noopener,noreferrer');
  };

  const handleOpenVerification = () => {
    if (!detail?.badge?.verification_url) return;
    window.open(detail.badge.verification_url, '_blank', 'noopener,noreferrer');
  };

  if (!canManageBadges) {
    return (
      <Container fluid className="page-container">
        <Alert variant="danger" className="alert-modern mt-4">
          هذه الشاشة متاحة فقط للأدوار الإدارية المناسبة.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container fluid className="page-container">
        <div className="dashboard-header-modern">
          <div className="header-content">
            <div className="header-icon"><FaIdCard /></div>
            <div>
              <h1 className="header-title">بطاقات التعريف</h1>
              <p className="header-subtitle">جاري تحميل بيانات البطاقات والموظفين</p>
            </div>
          </div>
        </div>
        <Card className="dashboard-card">
          <Card.Body className="p-5 text-center">
            <Spinner animation="border" />
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="page-container employee-badges-page">
      <div className="dashboard-header-modern employee-badges-header">
        <div className="header-content">
          <div className="header-icon"><FaIdCard /></div>
          <div>
            <h1 className="header-title">بطاقات التعريف</h1>
            <p className="header-subtitle">إنشاء وإدارة بطاقات الموظفين مع QR وصفحة تحقق عربية</p>
          </div>
        </div>
      </div>

      <Row className="g-3 employee-badges-stats">
        <Col md={3}>
          <Card className="employee-badges-stat-card">
            <Card.Body>
              <span className="employee-badges-stat-label">إجمالي الموظفين</span>
              <strong>{formatNumber(stats.total)}</strong>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="employee-badges-stat-card is-active">
            <Card.Body>
              <span className="employee-badges-stat-label">بطاقات فعالة</span>
              <strong>{formatNumber(stats.active)}</strong>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="employee-badges-stat-card is-warning">
            <Card.Body>
              <span className="employee-badges-stat-label">بطاقات معطلة</span>
              <strong>{formatNumber(stats.disabled)}</strong>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="employee-badges-stat-card is-muted">
            <Card.Body>
              <span className="employee-badges-stat-label">بدون بطاقة</span>
              <strong>{formatNumber(stats.none)}</strong>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {inlineMessage && (
        <Alert variant={inlineMessage.type} className="alert-modern mt-4">
          {inlineMessage.text}
        </Alert>
      )}

      <Row className="g-4 mt-1">
        <Col xl={4}>
          <Card className="dashboard-card employee-badges-users-card h-100">
            <Card.Body className="p-0">
              <div className="employee-badges-panel-head">
                <h3>الموظفون المؤهلون</h3>
                <Badge bg="light" text="dark">{filteredUsers.length}</Badge>
              </div>

              <div className="employee-badges-users-filter">
                <div className="employee-badges-search">
                  <FaSearch />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="ابحث بالاسم أو البريد أو رقم الموظف"
                  />
                </div>
                <Form.Select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  {STATUS_FILTERS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </div>

              <div className="employee-badges-user-list">
                {filteredUsers.length === 0 ? (
                  <div className="employee-badges-empty">
                    لا توجد نتائج مطابقة للفلاتر الحالية.
                  </div>
                ) : (
                  filteredUsers.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`employee-badges-user-item${Number(item.id) === Number(selectedUserId) ? ' is-active' : ''}`}
                      onClick={() => handleSelectUser(item.id)}
                    >
                      <div className="employee-badges-user-item__top">
                        <div>
                          <div className="employee-badges-user-item__name">{item.full_name}</div>
                          <div className="employee-badges-user-item__meta">{item.job_title || item.role_label_ar}</div>
                        </div>
                        <span className={`employee-badges-status-chip is-${getUserBadgeState(item)}`}>
                          {item.badge_status_label_ar}
                        </span>
                      </div>
                      <div className="employee-badges-user-item__foot">
                        <span>{item.staff_code}</span>
                        <span>{item.email}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={8}>
          <Card className="dashboard-card employee-badges-detail-card h-100">
            <Card.Body>
              {detailLoading ? (
                <div className="employee-badges-loading">
                  <Spinner animation="border" />
                </div>
              ) : detail ? (
                <Row className="g-4">
                  <Col lg={6}>
                    <EmployeeBadgeCard detail={previewDetail} />
                  </Col>

                  <Col lg={6}>
                    <div className="employee-badges-panel-head">
                      <h3>إدارة البطاقة</h3>
                      <Badge bg={detail.badge.badge_status === 'disabled' ? 'warning' : 'success'}>
                        {detail.badge.badge_status_label_ar}
                      </Badge>
                    </div>

                    <div className="employee-badges-actions">
                      <Button
                        className="employee-badges-btn-primary"
                        onClick={handleCreateBadge}
                        disabled={actionKey === 'create-badge'}
                      >
                        <FaUserCheck />
                        <span>
                          {detail.badge.exists
                            ? (detail.badge.badge_status === 'disabled' ? 'إعادة تفعيل البطاقة' : 'إعادة إصدار البطاقة')
                            : 'إنشاء البطاقة'}
                        </span>
                      </Button>

                      <Button
                        className="employee-badges-btn-secondary"
                        onClick={handleRegenerateQr}
                        disabled={!detail.badge.exists || actionKey === 'regenerate-qr'}
                      >
                        <FaRedo />
                        <span>إعادة توليد QR</span>
                      </Button>

                      <Button
                        className="employee-badges-btn-secondary"
                        onClick={handleDownloadPdf}
                        disabled={!detail.badge.exists || actionKey === 'download-pdf'}
                      >
                        <FaDownload />
                        <span>تحميل البطاقة</span>
                      </Button>

                      <Button
                        className="employee-badges-btn-secondary"
                        onClick={handlePrint}
                        disabled={!detail.badge.exists}
                      >
                        <FaPrint />
                        <span>طباعة البطاقة</span>
                      </Button>

                      <Button
                        className="employee-badges-btn-secondary"
                        onClick={handleOpenVerification}
                        disabled={!detail.badge.verification_url}
                      >
                        <FaEye />
                        <span>فتح صفحة التحقق</span>
                      </Button>

                      <Button
                        className="employee-badges-btn-danger"
                        onClick={handleDisableBadge}
                        disabled={!detail.badge.exists || detail.badge.badge_status === 'disabled' || actionKey === 'disable-badge'}
                      >
                        <FaTrash />
                        <span>تعطيل البطاقة</span>
                      </Button>
                    </div>

                    <div className="employee-badges-info-grid">
                      <div className="employee-badges-info-card">
                        <span>{'هاتف المشرف'}</span>
                        <strong dir="ltr">{detail.user.department_phone || '-'}</strong>
                      </div>
                      <div className="employee-badges-info-card">
                        <span>{'\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0634\u0631\u0643\u0629'}</span>
                        <strong>{detail.company_identity?.company_address_line || '-'}</strong>
                      </div>
                      <div className="employee-badges-info-card">
                        <span>{'\u0631\u0627\u0628\u0637 \u0627\u0644\u062a\u062d\u0642\u0642'}</span>
                        <strong className="employee-badges-link-text">
                          {detail.badge.verification_url ? <FaLink /> : '—'}
                        </strong>
                      </div>
                    </div>

                    <Form onSubmit={handleSaveProfile} className="employee-badges-form-block">
                      <div className="employee-badges-section-title">
                        <FaSave />
                        <span>بيانات البطاقة</span>
                      </div>
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>المسمى الوظيفي</Form.Label>
                            <Form.Control
                              value={profileForm.job_title}
                              onChange={(event) => setProfileForm((prev) => ({ ...prev, job_title: event.target.value }))}
                              placeholder="مثال: مهندس دعم فني"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>{'هاتف المشرف'}</Form.Label>
                            <Form.Control
                              value={profileForm.department_phone}
                              maxLength={30}
                              onChange={(event) => setProfileForm((prev) => ({ ...prev, department_phone: event.target.value }))}
                              placeholder="021-555-0100"
                              dir="ltr"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="employee-badges-form-actions">
                        <Button type="submit" className="employee-badges-btn-secondary" disabled={actionKey === 'save-profile'}>
                          <FaSave />
                          <span>حفظ البيانات</span>
                        </Button>
                      </div>
                    </Form>

                    <div className="employee-badges-form-block">
                      <div className="employee-badges-section-title">
                        <FaCamera />
                        <span>{'\u0634\u0639\u0627\u0631 \u0627\u0644\u0634\u0631\u0643\u0629'}</span>
                      </div>
                      <p className="employee-badges-help-text">
                        {'\u064a\u064f\u0633\u062a\u062e\u062f\u0645 \u0647\u0630\u0627 \u0627\u0644\u0634\u0639\u0627\u0631 \u062a\u0644\u0642\u0627\u0626\u064a\u064b\u0627 \u0641\u064a \u0627\u0644\u0645\u0639\u0627\u064a\u0646\u0629 \u0648\u0627\u0644\u0637\u0628\u0627\u0639\u0629 \u0648\u0627\u0644\u062a\u0635\u062f\u064a\u0631 PDF \u0648\u0635\u0641\u062d\u0629 \u0627\u0644\u062a\u062d\u0642\u0642.'}
                      </p>
                      <div className="employee-badges-logo-preview">
                        {previewDetail?.company_identity?.logo_data_uri ? (
                          <img
                            src={previewDetail.company_identity.logo_data_uri}
                            alt={('\u0634\u0639\u0627\u0631 \u0627\u0644\u0634\u0631\u0643\u0629')}
                          />
                        ) : (
                          <div className="employee-badges-logo-preview__placeholder">
                            {'\u0644\u0627 \u064a\u0648\u062c\u062f \u0634\u0639\u0627\u0631 \u0645\u062d\u0641\u0648\u0638 \u062d\u0627\u0644\u064a\u064b\u0627.'}
                            <br />
                            {'\u0627\u062e\u062a\u0631 \u0635\u0648\u0631\u0629 \u0634\u0639\u0627\u0631 \u0644\u0625\u0638\u0647\u0627\u0631\u0647\u0627 \u0641\u064a \u062c\u0645\u064a\u0639 \u0627\u0644\u0628\u0637\u0627\u0642\u0627\u062a.'}
                          </div>
                        )}
                      </div>
                      <div className="employee-badges-photo-actions">
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                          className="d-none"
                          onChange={handleLogoSelected}
                        />
                        <Button className="employee-badges-btn-secondary" onClick={handleLogoPick}>
                          <FaCamera />
                          <span>
                            {pendingLogo
                              ? '\u062a\u063a\u064a\u064a\u0631 \u0627\u0644\u0634\u0639\u0627\u0631 \u0627\u0644\u0645\u062e\u062a\u0627\u0631'
                              : (detail.company_identity?.has_custom_logo
                                  ? '\u0627\u0633\u062a\u0628\u062f\u0627\u0644 \u0627\u0644\u0634\u0639\u0627\u0631'
                                  : '\u0627\u062e\u062a\u064a\u0627\u0631 \u0634\u0639\u0627\u0631')}
                          </span>
                        </Button>
                        <Button
                          className="employee-badges-btn-primary"
                          onClick={handleUploadLogo}
                          disabled={!pendingLogo?.file || actionKey === 'upload-logo'}
                        >
                          <FaSave />
                          <span>{'\u0631\u0641\u0639 \u0627\u0644\u0634\u0639\u0627\u0631'}</span>
                        </Button>
                        <Button
                          className="employee-badges-btn-danger"
                          onClick={handleDeleteLogo}
                          disabled={!pendingLogo && !detail.company_identity?.has_custom_logo}
                        >
                          <FaTrash />
                          <span>{'\u062d\u0630\u0641 \u0627\u0644\u0634\u0639\u0627\u0631'}</span>
                        </Button>
                      </div>
                    </div>

                    <div className="employee-badges-form-block">
                      <div className="employee-badges-section-title">
                        <FaCamera />
                        <span>صورة الموظف</span>
                      </div>
                      <p className="employee-badges-help-text">
                        الصيغ المسموحة: JPG وJPEG وPNG. يتم تجهيز الصورة تلقائيًا لتناسب البطاقة والطباعة.
                      </p>
                      <div className="employee-badges-photo-actions">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                          className="d-none"
                          onChange={handlePhotoSelected}
                        />
                        <Button className="employee-badges-btn-secondary" onClick={handlePhotoPick}>
                          <FaCamera />
                          <span>{pendingPhoto ? 'تغيير الصورة المختارة' : (detail.user.profile_image_url ? 'تغيير الصورة' : 'اختيار صورة')}</span>
                        </Button>
                        <Button
                          className="employee-badges-btn-primary"
                          onClick={handleUploadPhoto}
                          disabled={!pendingPhoto?.file || actionKey === 'upload-photo'}
                        >
                          <FaSave />
                          <span>رفع الصورة</span>
                        </Button>
                        <Button
                          className="employee-badges-btn-danger"
                          onClick={handleDeletePhoto}
                          disabled={!pendingPhoto && !detail.user.profile_image_url}
                        >
                          <FaTrash />
                          <span>حذف الصورة</span>
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              ) : (
                <div className="employee-badges-empty">
                  لا يوجد موظفون مؤهلون حاليًا لإنشاء بطاقات تعريف.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EmployeeBadges;
